import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { useAuthStore } from '../../../stores/authStore'
import { useToastStore } from '../../../stores/toastStore'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { UserBadge } from '../../../components/auth/UserBadge'
import { StratIQXHeader } from '../../../components/ui/StratIQXHeader'
import { AutosaveIndicator } from '../../../components/ui/AutosaveIndicator'
import { ExitConfirmationDialog } from '../../../components/modals/ExitConfirmationDialog'
import { PricingPreview } from './PricingPreview'
import { ChevronLeft, ChevronRight, CheckCircle, AlertTriangle, ArrowLeft, CreditCard } from 'lucide-react'
import { EnterpriseProgressBar } from './EnterpriseProgressBar'

interface OnboardingLayoutProps {
  children: React.ReactNode
}

export const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({ children }) => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { addToast } = useToastStore()
  const { 
    currentScreen, 
    getProgress, 
    nextScreen, 
    previousScreen, 
    canProceed,
    validateCurrentScreen,
    triggerAutosave,
    lastSaved,
    isSaving,
    hasUnsavedChanges,
    saveError
  } = useOnboardingStore()

  const [showExitDialog, setShowExitDialog] = useState(false)
  const [showPricingCard, setShowPricingCard] = useState(false)
  const [autosaveTimer, setAutosaveTimer] = useState<NodeJS.Timeout | null>(null)

  const progress = getProgress()
  const validation = validateCurrentScreen()
  const hasValidationErrors = !validation.isValid && validation.errors.length > 0
  const isFirstScreen = currentScreen === 'industry-selection'
  const isLastScreen = currentScreen === 'intelligence-summary'

  // Set up autosave timer (every 30 seconds)
  useEffect(() => {
    if (hasUnsavedChanges && !isSaving) {
      if (autosaveTimer) {
        clearTimeout(autosaveTimer)
      }
      
      const timer = setTimeout(() => {
        triggerAutosave()
      }, 30000) // 30 seconds
      
      setAutosaveTimer(timer)
    }

    return () => {
      if (autosaveTimer) {
        clearTimeout(autosaveTimer)
      }
    }
  }, [hasUnsavedChanges, isSaving])

  // Show save error toast
  useEffect(() => {
    if (saveError) {
      addToast({
        type: 'error',
        title: 'Failed to save progress',
        description: 'Your changes could not be saved. Please check your connection and try again.',
        duration: 5000
      })
    }
  }, [saveError, addToast])

  const handleNext = () => {
    if (canProceed()) {
      nextScreen()
    }
  }

  const handlePrevious = () => {
    previousScreen()
  }

  const handleBackToHome = () => {
    if (hasUnsavedChanges) {
      setShowExitDialog(true)
    } else {
      navigate('/')
    }
  }

  const handleExitConfirm = () => {
    // Trigger one final save before leaving
    triggerAutosave()
    setShowExitDialog(false)
    navigate('/')
  }

  const handleReviewAndSelectPlan = () => {
    // Trigger final save
    triggerAutosave()
    
    // Show success toast
    addToast({
      type: 'success',
      title: 'Configuration Complete!',
      description: 'Redirecting to plan selection and payment...',
      duration: 3000
    })
    
    // Navigate to payment page after a short delay
    setTimeout(() => {
      navigate('/payment')
    }, 1500)
  }

  // Calculate total steps including payment
  const currentStepNumber = {
    'industry-selection': 1,
    'business-profile': 2,
    'revenue-intelligence': 3,
    'supply-chain-intelligence': 4,
    'competitive-intelligence': 5,
    'data-integration': 6,
    'intelligence-delivery': 7,
    'intelligence-summary': 8
  }[currentScreen] || 1

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Enhanced Header with Navigation, Autosave, and Pricing */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            {/* Left side - Back to Home and Autosave */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={handleBackToHome}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
              
              {/* Autosave Indicator */}
              <div className="hidden sm:block">
                <AutosaveIndicator
                  lastSaved={lastSaved}
                  isSaving={isSaving}
                  hasError={saveError}
                />
              </div>
            </div>
            
            {/* Right side - User Badge (if authenticated) */}
            {isAuthenticated && <UserBadge />}
          </div>
          
          {/* Pricing Preview Bar */}
          <div className="mb-4">
            <PricingPreview compact className="w-full" />
          </div>
          
          {/* Mobile Autosave Indicator */}
          <div className="sm:hidden mb-2">
            <AutosaveIndicator
              lastSaved={lastSaved}
              isSaving={isSaving}
              hasError={saveError}
              className="text-xs"
            />
          </div>
          
          {/* Center - Title */}
          <StratIQXHeader
            title="Strategic Intelligence Setup"
            description="Complete 8 configuration steps, then select your service plan"
            compact={true}
          />
        </div>

        {/* Enhanced Enterprise Progress Section with Payment Step */}
        <div className="mb-6">
          <EnterpriseProgressBar 
            currentScreen={currentScreen}
            totalProgress={progress}
            hasValidationErrors={hasValidationErrors}
          />
          
          {/* Payment Step Indicator */}
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-600">
            <span className="font-medium">Configuration: Step {currentStepNumber} of 8</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <CreditCard className="w-4 h-4 text-gray-500" />
              Payment & Activation comes next
            </span>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-lg border-0">
            <CardContent className="p-8">
              {children}
            </CardContent>
          </Card>
          
          {/* Pricing Card Toggle Button - Mobile */}
          <div className="lg:hidden mt-4 text-center">
            <Button
              variant="ghost"
              onClick={() => setShowPricingCard(!showPricingCard)}
              className="text-blue-600 hover:text-blue-800"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {showPricingCard ? 'Hide' : 'Show'} Pricing Info
            </Button>
          </div>
          
          {/* Mobile Pricing Card */}
          {showPricingCard && (
            <div className="lg:hidden mt-4">
              <PricingPreview />
            </div>
          )}
        </div>

        {/* Desktop Pricing Sidebar */}
        <div className="hidden lg:block fixed right-8 top-32 w-72">
          <PricingPreview />
        </div>

        {/* Enhanced Navigation */}
        <div className="max-w-4xl mx-auto mt-8">
          <Card className="shadow-lg border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={isFirstScreen}
                  className="flex items-center gap-2 px-6 py-3 font-medium"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous Step
                </Button>

                <div className="flex-1 mx-6">
                  {hasValidationErrors && (
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                        <div className="text-sm text-red-800">
                          <div className="font-semibold mb-2 flex items-center gap-2">
                            Complete Required Fields
                          </div>
                          <ul className="space-y-1">
                            {validation.errors.map((error, index) => (
                              <li key={index} className="flex items-center gap-2">
                                <div className="h-1.5 w-1.5 bg-red-500 rounded-full shrink-0" />
                                {error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {validation.isValid && !isLastScreen && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <div className="text-sm text-green-800">
                          <span className="font-semibold">Ready to proceed!</span>
                          <span className="ml-2">All required fields are completed.</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enhanced Next Button */}
                {!isLastScreen && (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`flex items-center gap-2 px-6 py-3 font-medium transition-all duration-200 ${
                      canProceed() 
                        ? 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg' 
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    Continue Setup
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
                
                {/* Final Screen - Updated Button Text */}
                {isLastScreen && (
                  <Button
                    onClick={handleReviewAndSelectPlan}
                    className="flex items-center gap-2 px-8 py-3 font-medium bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <CreditCard className="h-4 w-4" />
                    Review & Select Plan
                  </Button>
                )}

                {/* Maintain layout spacing on final screen when no Next button */}
                {isLastScreen && <div />}
              </div>

              {/* Progress Summary Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-4">
                    <span>Configuration: {progress}% complete</span>
                    <span>•</span>
                    <span>Step {currentStepNumber} of 8</span>
                    {!isLastScreen && (
                      <>
                        <span>•</span>
                        <span>Payment after completion</span>
                      </>
                    )}
                  </div>
                  <div className="text-blue-600 font-medium">
                    {isLastScreen ? 'Almost there!' : 'Building your StratIQX Platform'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <ExitConfirmationDialog
        isOpen={showExitDialog}
        onConfirm={handleExitConfirm}
        onCancel={() => setShowExitDialog(false)}
        hasUnsavedChanges={hasUnsavedChanges}
      />
    </div>
  )
}